import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faFileExcel, faUpload } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { bulkImportService, type BulkImportSession } from '@/services/bulk-import.service';
import { getApiErrorMessage } from '@/lib/api-error';
import { toast } from '@/lib/swal';

const STATUS_FALLBACKS: Record<number, string> = {
  400: 'The uploaded file could not be read. Please check it matches the template and try again.',
  403: 'You do not have permission to upload to this import session.',
  404: 'This import session no longer exists.',
  409: 'This session is not in a state that accepts an upload right now.',
  413: 'The file is too large to upload.',
  500: 'Something went wrong while uploading the file. Please try again.',
};

interface BulkImportUploadStageProps {
  session: BulkImportSession;
}

export function BulkImportUploadStage({ session }: BulkImportUploadStageProps) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (f: File) => bulkImportService.upload(session.id, f),
    onSuccess: () => {
      toast.success('Workbook uploaded — validation started');
      void queryClient.invalidateQueries({ queryKey: ['bulk-import-session', session.id] });
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      const fallback = (status && STATUS_FALLBACKS[status]) || 'Failed to upload workbook.';
      toast.error(getApiErrorMessage(error, fallback));
    },
  });

  const handleFileSelect = (selected: File | null) => {
    setLocalError(null);
    if (!selected) {
      setFile(null);
      return;
    }
    if (!/\.xlsx$/i.test(selected.name)) {
      setLocalError('Only .xlsx workbooks are accepted.');
      setFile(null);
      return;
    }
    setFile(selected);
  };

  const handleSubmit = () => {
    if (!file || uploadMutation.isPending) return;
    uploadMutation.mutate(file);
  };

  return (
    <div className="max-w-2xl space-y-4 rounded-lg border border-border bg-card p-5">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Upload Completed Workbook</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Upload the .xlsx template you downloaded and filled in for this session. It will be
          validated automatically once uploaded.
        </p>
      </div>

      <label
        htmlFor="bi-upload-file"
        className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/20 px-6 py-10 text-center transition-colors hover:border-primary/50"
      >
        <FontAwesomeIcon icon={faFileExcel} className="h-8 w-8 text-muted-foreground/60" />
        <span className="text-sm font-medium text-foreground">
          {file ? file.name : 'Click to choose an .xlsx file'}
        </span>
        <span className="text-xs text-muted-foreground">Only .xlsx workbooks are accepted</span>
        <input
          ref={inputRef}
          id="bi-upload-file"
          type="file"
          accept=".xlsx"
          className="sr-only"
          onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
          disabled={uploadMutation.isPending}
        />
      </label>

      {localError && <p className="text-xs text-destructive">{localError}</p>}

      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={!file || uploadMutation.isPending}>
          {uploadMutation.isPending ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faUpload} className="mr-2 h-3.5 w-3.5" />
              Upload & Validate
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
